import { useEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { ChevronDown, Check, EyeOff } from 'lucide-react';
import type { Artist, Booking } from '@/domain/types';
import { bookingStatusTone, type DepositLevel } from '@/utils/statusTone';
import { type CalendarFilters, EMPTY_CALENDAR_FILTERS } from '@/utils/calendarFilters';

const STATUS_OPTIONS: Booking['status'][] = [
  'held',
  'pending-deposit',
  'confirmed',
  'rescheduled',
  'checked-in',
  'in-chair',
  'completed',
  'cancelled',
  'no-show',
];

const DEPOSIT_OPTIONS: Array<{ level: DepositLevel; label: string }> = [
  { level: 'full', label: 'Paid in full' },
  { level: 'partial', label: 'Part-paid' },
  { level: 'none', label: 'Awaiting' },
];

function toggle<T>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

/** A compact multi-select dropdown: trigger with active-count badge + checklist popover. */
function FilterDropdown({
  label,
  count,
  testId,
  children,
}: {
  label: string;
  count: number;
  testId: string;
  children: ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (e: Event) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('pointerdown', outside);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('pointerdown', outside);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const active = count > 0;
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="true"
        aria-expanded={open}
        data-testid={testId}
        className={clsx(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition',
          active
            ? 'border-gold-bright text-gold-bright bg-gold/10'
            : 'border-gold-dim/40 text-cream-soft hover:border-gold-bright/60',
        )}
      >
        {label}
        {active ? (
          <span className="font-mono text-[10px] leading-none px-1.5 py-0.5 rounded-full bg-gold-bright/20 text-gold-bright">
            {count}
          </span>
        ) : null}
        <ChevronDown size={12} className={clsx('transition', open ? 'rotate-180' : '')} />
      </button>
      {open ? (
        <div className="absolute left-0 mt-2 w-56 z-40 panel py-1 shadow-ink-soft max-h-72 overflow-y-auto">
          {children}
        </div>
      ) : null}
    </div>
  );
}

function OptionRow({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      data-testid={testId}
      className={clsx(
        'w-full px-3 py-1.5 flex items-center gap-2.5 text-left hover:bg-ink-soft/50 transition',
        active ? 'text-gold-bright' : 'text-cream-soft',
      )}
    >
      <span
        className={clsx(
          'h-3.5 w-3.5 rounded-[3px] border flex items-center justify-center shrink-0',
          active ? 'border-gold-bright bg-gold/25' : 'border-gold-dim/50',
        )}
      >
        {active ? <Check size={10} className="text-gold-bright" /> : null}
      </span>
      {children}
    </button>
  );
}

interface Props {
  artists: Artist[];
  filters: CalendarFilters;
  onChange: (next: CalendarFilters) => void;
  /**
   * Show the artist multi-select. Off in Week view, where the header's
   * single-select artist switcher owns artist selection instead.
   */
  showArtistFilter?: boolean;
}

/**
 * Calendar filter bar (brief item 7). Compact multi-select dropdowns for status,
 * deposit state and artist — collapsed from rows of chips so the bar stays quiet
 * even with 20+ artists rostered — plus a "hide green/filled" toggle so only
 * bookings needing action show (CU 86d3eeuqw).
 */
export default function CalendarFilterBar({
  artists,
  filters,
  onChange,
  showArtistFilter = true,
}: Props) {
  const anyActive =
    filters.statuses.length > 0 ||
    filters.depositLevels.length > 0 ||
    filters.artistIds.length > 0 ||
    filters.hideFilled;

  return (
    <div
      className="panel px-3 py-2 mb-4 flex items-center gap-2 flex-wrap"
      data-testid="calendar-filter-bar"
    >
      <span className="label-small-caps text-cream-soft mr-1">Filter</span>

      <FilterDropdown label="Status" count={filters.statuses.length} testId="filter-dd-status">
        {STATUS_OPTIONS.map((s) => (
          <OptionRow
            key={s}
            active={filters.statuses.includes(s)}
            onClick={() => onChange({ ...filters, statuses: toggle(filters.statuses, s) })}
            testId={`filter-status-${s}`}
          >
            <span className={clsx('h-2 w-2 rounded-full shrink-0', bookingStatusTone(s).dot)} />
            <span className="text-sm">{bookingStatusTone(s).label}</span>
          </OptionRow>
        ))}
      </FilterDropdown>

      <FilterDropdown label="Deposit" count={filters.depositLevels.length} testId="filter-dd-deposit">
        {DEPOSIT_OPTIONS.map((d) => (
          <OptionRow
            key={d.level}
            active={filters.depositLevels.includes(d.level)}
            onClick={() =>
              onChange({ ...filters, depositLevels: toggle(filters.depositLevels, d.level) })
            }
            testId={`filter-deposit-${d.level}`}
          >
            <span className="text-sm">{d.label}</span>
          </OptionRow>
        ))}
      </FilterDropdown>

      {showArtistFilter ? (
        <FilterDropdown label="Artist" count={filters.artistIds.length} testId="filter-dd-artist">
          {artists.length === 0 ? (
            <p className="px-3 py-2 font-serif text-sm text-cream-soft">No artists.</p>
          ) : (
            artists.map((a) => (
              <OptionRow
                key={a.id}
                active={filters.artistIds.includes(a.id)}
                onClick={() => onChange({ ...filters, artistIds: toggle(filters.artistIds, a.id) })}
                testId={`filter-artist-${a.id}`}
              >
                <span className="text-sm">{a.displayName}</span>
              </OptionRow>
            ))
          )}
        </FilterDropdown>
      ) : null}

      <button
        type="button"
        onClick={() => onChange({ ...filters, hideFilled: !filters.hideFilled })}
        aria-pressed={filters.hideFilled}
        data-testid="filter-hide-filled"
        className={clsx(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border text-xs transition',
          filters.hideFilled
            ? 'border-mint text-mint bg-mint/10'
            : 'border-gold-dim/40 text-cream-soft hover:border-gold-bright/60',
        )}
      >
        <EyeOff size={12} /> Hide filled
      </button>

      {anyActive ? (
        <button
          type="button"
          onClick={() => onChange(EMPTY_CALENDAR_FILTERS)}
          data-testid="filter-clear"
          className="ml-auto text-[11px] text-cream-faint hover:text-cream-soft underline underline-offset-2"
        >
          Clear filters
        </button>
      ) : null}
    </div>
  );
}
